import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import { abortPasswordReset, selectForgotPasswordData } from 'store/forgotPassword/forgotPassword.slice';
import { CustomInput } from 'components/CustomInput/CustomInput';
import { CustomButton } from 'components/CustomButton/CustomButton';
import { getRequiredRule, passwordRules, retypePasswordRules } from 'utils/helpers/validationRules';
import { SFields, SSubmitError } from './ChangePasswordForm.styles';
import { useFormHelpers } from 'utils/hooks/useFormHelpers';
import { useFormValidationHelper } from 'utils/hooks/useFormValidationHelper';
import { IChangePasswordFormValues } from '../ChangePasswordPage.types';
import { ROUTES } from 'constants/routes';
import { isErrorWithMessage, isFetchBaseQueryError } from 'utils/rtqErrorHandling';
import { getFingerPrint } from 'utils/webID';
import { getClientDateTime } from '../../../utils/common';

interface IChangePasswordFormProps {
  handleChangePassword: Function;
  isLoading: boolean;
}

export const ChangePasswordForm: React.FC<IChangePasswordFormProps> = ({ handleChangePassword, isLoading = true }) => {
  const navigate = useNavigate();
  const { transactionId } = useSelector(selectForgotPasswordData);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const { hasErrors, checkErrors } = useFormHelpers(form);
  const { isDisabled, passwordErrors, handleFormChange, formValidationHelpStates } = useFormValidationHelper(form);

  const onFinish = async (values: IChangePasswordFormValues) => {
    if (!transactionId) return;

    try {
      await handleChangePassword({
        fingerprint: getFingerPrint(true) as string,
        transactionId,
        password: values.newPassword,
        clientDateTime: getClientDateTime(),
      }).unwrap();

      dispatch(abortPasswordReset());
      await navigate(ROUTES.success.path);
    } catch (err) {
      if (isFetchBaseQueryError(err)) {
        const errorMessage = 'error' in err ? err.error : JSON.stringify(err.data);
        try {
          const parsedError = JSON.parse(errorMessage || '{}');
          setError(parsedError.Error || 'Unknown error');
        } catch {
          setError('Unknown error');
        }
      } else if (isErrorWithMessage(err)) {
        setError(err.message);
      }
    }
  };

  return (
    <Form autoComplete="off" layout="vertical" requiredMark={false} form={form} onFieldsChange={checkErrors} onFinish={onFinish}>
      <SFields>
        <Form.Item name="newPassword" validateTrigger={['onBlur', 'onChange']} label="New Password" help="" rules={[...passwordRules, getRequiredRule('Please input your password')]}>
          <CustomInput
            type="password"
            placeholder="*************"
            bgColor="white"
            data-testid="newPassword"
            onKeyUp={handleFormChange}
            isError={passwordErrors.length > 0 && form.getFieldValue('newPassword')?.length > 0}
            isSuccess={passwordErrors.length === 0 && form.getFieldValue('newPassword')?.length > 0}
          />
        </Form.Item>

        <Form.Item name="retypePassword" validateTrigger={['onBlur', 'onChange']} label="Retype New Password" help={formValidationHelpStates} rules={retypePasswordRules}>
          <CustomInput
            type="password"
            placeholder="*************"
            bgColor="white"
            data-testid="retypePassword"
            onKeyUp={handleFormChange}
            isError={hasErrors && form.getFieldValue('retypePassword')?.length > 0}
            isSuccess={!hasErrors && form.getFieldValue('retypePassword')?.length > 0}
          />
        </Form.Item>
      </SFields>

      <Form.Item>
        <SSubmitError>{error ?? ''}</SSubmitError>

        <CustomButton type="primary" block buttonSize="large" htmlType="submit" disabled={isLoading || isDisabled}>
          Confirm
        </CustomButton>
      </Form.Item>
    </Form>
  );
}; 